import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronRight, RefreshCw } from 'lucide-react';
import { useFilesManagerSettings, useFilesManagerRuns } from '../../hooks/useFilesManager';
import { useTenants } from '../../hooks/useAdmin';
import { FilesManagerModel, FilesManagerRun } from '../../types/admin';

const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1';

const SELECT_CLASS = `w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent`;

const SECONDARY_BUTTON_CLASS = `inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${FOCUS_RING}`;

const STAGE_LABELS: Record<string, string> = {
  page_detection: 'Page detection',
  ocr: 'OCR',
  classification: 'Classification',
  extraction: 'Extraction',
  localization: 'Field locations',
};

const PROVIDER_LABELS: Record<string, string> = {
  bedrock: 'Bedrock',
  google: 'Google',
};

const providerLabel = (provider: string) =>
  PROVIDER_LABELS[provider] ||
  (provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'Other');

const modelOptionLabel = (model: FilesManagerModel) =>
  `${model.label} (${providerLabel(model.provider)})`;

const stageLabel = (stage: string) => {
  if (STAGE_LABELS[stage]) return STAGE_LABELS[stage];
  const spaced = stage.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : stage;
};

/** Format milliseconds as e.g. "1m 12s". */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 1) return ms > 0 ? '<1s' : '0s';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatNumber = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString();

const RunStatusPill: React.FC<{ status: string }> = ({ status }) => {
  let classes = 'bg-violet-100 text-violet-700';
  let label = 'Running';
  if (status === 'SUCCESS') {
    classes = 'bg-green-100 text-green-800';
    label = 'Success';
  } else if (status === 'FAILED') {
    classes = 'bg-red-100 text-red-800';
    label = 'Failed';
  } else if (status !== 'RUNNING') {
    classes = 'bg-gray-100 text-gray-600';
    label = status ? status.charAt(0) + status.slice(1).toLowerCase() : 'Unknown';
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold ${classes}`}>
      {label}
    </span>
  );
};

const TH_CLASS = 'px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap';

interface RunRowProps {
  run: FilesManagerRun;
  expanded: boolean;
  onToggle: () => void;
}

const RunRow: React.FC<RunRowProps> = ({ run, expanded, onToggle }) => {
  const fileLabel = run.fileName || run.fileId || '—';
  const detailsId = `files-manager-run-${run.invocationId}`;
  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="pl-4 py-3 w-10">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={detailsId}
            aria-label={`${expanded ? 'Hide' : 'Show'} stage breakdown for ${fileLabel}`}
            className={`p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 ${FOCUS_RING}`}
          >
            <ChevronRight
              size={16}
              strokeWidth={2}
              className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
              aria-hidden="true"
            />
          </button>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
          {formatDateTime(run.startedAt)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-900 max-w-[220px] truncate" title={fileLabel}>
          {fileLabel}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <RunStatusPill status={run.status} />
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{run.visionModelId}</td>
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{run.extractionModelId}</td>
        <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
          {formatNumber(run.inputTokens)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
          {formatNumber(run.outputTokens)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-900 font-bold text-right whitespace-nowrap">
          {formatNumber(run.totalTokens)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
          {formatDuration(run.durationMs)}
        </td>
      </tr>
      {expanded && (
        <tr id={detailsId} className="bg-gray-50">
          <td colSpan={10} className="px-4 py-4">
            <div className="pl-10 space-y-3">
              {run.stages.length === 0 ? (
                <p className="text-sm text-gray-500">No stage usage recorded for this run.</p>
              ) : (
                <table className="min-w-[560px] text-left">
                  <caption className="sr-only">Stage breakdown for {fileLabel}</caption>
                  <thead>
                    <tr>
                      <th scope="col" className={TH_CLASS}>Stage</th>
                      <th scope="col" className={TH_CLASS}>Model</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Input tokens</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Output tokens</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.stages.map((stage) => (
                      <tr key={`${stage.stage}-${stage.modelId}`} className="border-t border-gray-200">
                        <td className="px-4 py-2 text-sm text-gray-900">{stageLabel(stage.stage)}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{stage.modelId}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">
                          {formatNumber(stage.inputTokens)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">
                          {formatNumber(stage.outputTokens)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">
                          {formatNumber(stage.requests)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {run.status === 'FAILED' && run.errorMessage && (
                <p className="text-sm text-gray-600">
                  <span className="font-bold text-gray-700">Error: </span>
                  {run.errorMessage}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * FilesManagerPage - Platform admin settings for the Files manager models
 * and per-tenant run/token usage.
 */
const FilesManagerPage: React.FC = () => {
  const {
    data: settings,
    loading: settingsLoading,
    error: settingsError,
    saving,
    saveError,
    fetchSettings,
    saveSettings,
  } = useFilesManagerSettings();
  const { data: tenants, fetchTenants } = useTenants(100);

  const [visionModelId, setVisionModelId] = useState('');
  const [extractionModelId, setExtractionModelId] = useState('');
  const [saved, setSaved] = useState(false);

  const [tenantId, setTenantId] = useState('');
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const { runs, loading: runsLoading, error: runsError, nextToken, fetchRuns, reset } =
    useFilesManagerRuns(tenantId || null, 25);

  useEffect(() => {
    fetchSettings();
    fetchTenants();
  }, [fetchSettings, fetchTenants]);

  // Sync the form with the loaded (or just saved) settings
  useEffect(() => {
    if (settings) {
      setVisionModelId(settings.visionModelId);
      setExtractionModelId(settings.extractionModelId);
    }
  }, [settings]);

  useEffect(() => {
    setExpandedRuns(new Set());
    reset();
    if (tenantId) {
      fetchRuns();
    }
  }, [tenantId, fetchRuns, reset]);

  const visionModels = useMemo(
    () => (settings?.availableModels || []).filter((m) => m.supportsVision),
    [settings]
  );
  const allModels = settings?.availableModels || [];

  const isDirty =
    !!settings &&
    (visionModelId !== settings.visionModelId || extractionModelId !== settings.extractionModelId);
  const saveDisabled = saving || !isDirty;
  const saveTitle = saving
    ? 'Saving the models'
    : !isDirty
      ? 'Change a model to save'
      : undefined;

  const handleSave = async () => {
    if (saveDisabled) return;
    setSaved(false);
    const result = await saveSettings({ visionModelId, extractionModelId });
    setSaved(Boolean(result));
  };

  const toggleRun = (id: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totals = useMemo(
    () =>
      runs.reduce(
        (acc, run) => ({
          input: acc.input + (run.inputTokens || 0),
          output: acc.output + (run.outputTokens || 0),
        }),
        { input: 0, output: 0 }
      ),
    [runs]
  );

  const updatedMeta =
    settings?.updatedAt && settings.updatedBy
      ? `Updated ${formatDateTime(settings.updatedAt)} by ${settings.updatedBy}`
      : settings?.updatedAt
        ? `Updated ${formatDateTime(settings.updatedAt)}`
        : null;

  return (
    <div className="space-y-6 min-w-[1100px]">
      {/* Header */}
      <div>
        <h1 className="text-[28px] leading-tight font-bold text-gray-900">Files manager</h1>
        <p className="mt-1 text-sm text-gray-600">
          Choose the models used to process files and review token usage by tenant
        </p>
      </div>

      {/* Models card */}
      <section
        aria-labelledby="files-manager-models-title"
        className="bg-white rounded-xl border border-gray-200 p-[18px] space-y-4"
      >
        <h2 id="files-manager-models-title" className="text-base font-bold text-gray-900">
          Models
        </h2>

        {settingsLoading ? (
          <div className="flex items-center justify-center py-8" role="status" aria-label="Loading models">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
          </div>
        ) : settingsError ? (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {settingsError}
          </div>
        ) : settings ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="files-manager-vision-model" className="block text-sm font-bold text-gray-700">
                  Vision model
                </label>
                <p id="files-manager-vision-help" className="text-xs text-gray-500 mb-1.5">
                  Page detection and OCR
                </p>
                <select
                  id="files-manager-vision-model"
                  aria-describedby="files-manager-vision-help"
                  value={visionModelId}
                  onChange={(e) => {
                    setVisionModelId(e.target.value);
                    setSaved(false);
                  }}
                  className={SELECT_CLASS}
                >
                  {visionModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {modelOptionLabel(model)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="files-manager-extraction-model" className="block text-sm font-bold text-gray-700">
                  Extraction model
                </label>
                <p id="files-manager-extraction-help" className="text-xs text-gray-500 mb-1.5">
                  Classification and field extraction
                </p>
                <select
                  id="files-manager-extraction-model"
                  aria-describedby="files-manager-extraction-help"
                  value={extractionModelId}
                  onChange={(e) => {
                    setExtractionModelId(e.target.value);
                    setSaved(false);
                  }}
                  className={SELECT_CLASS}
                >
                  {allModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {modelOptionLabel(model)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Google models need the Gemini API key set in Secrets Manager.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saveDisabled}
                aria-disabled={saveDisabled}
                title={saveTitle}
                style={saveDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 transition-colors ${FOCUS_RING}`}
              >
                {saving ? 'Saving…' : 'Save models'}
              </button>
              {saved && !saveError && (
                <span role="status" className="inline-flex items-center gap-1.5 text-sm text-green-800">
                  <Check size={16} strokeWidth={2} aria-hidden="true" />
                  Models saved
                </span>
              )}
              {saveError && (
                <span role="alert" className="text-sm text-red-700">
                  {saveError}
                </span>
              )}
              {updatedMeta && <span className="text-xs text-gray-500">{updatedMeta}</span>}
            </div>
          </>
        ) : null}
      </section>

      {/* Runs card */}
      <section
        aria-labelledby="files-manager-runs-title"
        className="bg-white rounded-xl border border-gray-200 p-[18px] space-y-4"
      >
        <div className="flex items-end justify-between gap-4">
          <div className="flex-1 max-w-sm">
            <h2 id="files-manager-runs-title" className="text-base font-bold text-gray-900 mb-3">
              Runs and token usage
            </h2>
            <label htmlFor="files-manager-tenant" className="block text-sm font-bold text-gray-700 mb-1.5">
              Tenant
            </label>
            <select
              id="files-manager-tenant"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="">Choose a tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => fetchRuns()}
            disabled={!tenantId || runsLoading}
            title={!tenantId ? 'Choose a tenant to refresh its runs' : undefined}
            className={SECONDARY_BUTTON_CLASS}
          >
            <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
            Refresh
          </button>
        </div>

        {!tenantId ? (
          <p className="py-10 text-center text-sm text-gray-500">Choose a tenant to see its runs</p>
        ) : (
          <>
            {runsError && (
              <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                {runsError}
              </div>
            )}

            <p className="text-sm text-gray-700" aria-live="polite">
              <span className="font-bold">
                {runs.length} {runs.length === 1 ? 'run' : 'runs'}
              </span>
              <span className="mx-2 text-gray-300" aria-hidden="true">·</span>
              {formatNumber(totals.input)} input tokens
              <span className="mx-2 text-gray-300" aria-hidden="true">·</span>
              {formatNumber(totals.output)} output tokens
            </p>

            {runsLoading && runs.length === 0 ? (
              <div className="flex items-center justify-center py-10" role="status" aria-label="Loading runs">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
              </div>
            ) : runs.length === 0 && !runsError ? (
              <p className="py-10 text-center text-sm text-gray-500">No runs yet for this tenant</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="min-w-full text-left">
                  <caption className="sr-only">Files manager runs and token usage</caption>
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="pl-4 py-3 w-10">
                        <span className="sr-only">Stage breakdown</span>
                      </th>
                      <th scope="col" className={TH_CLASS}>Started</th>
                      <th scope="col" className={TH_CLASS}>File</th>
                      <th scope="col" className={TH_CLASS}>Status</th>
                      <th scope="col" className={TH_CLASS}>Vision model</th>
                      <th scope="col" className={TH_CLASS}>Extraction model</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Input tokens</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Output tokens</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Total tokens</th>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <RunRow
                        key={run.invocationId}
                        run={run}
                        expanded={expandedRuns.has(run.invocationId)}
                        onToggle={() => toggleRun(run.invocationId)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {nextToken && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => fetchRuns(nextToken)}
                  disabled={runsLoading}
                  className={SECONDARY_BUTTON_CLASS}
                >
                  {runsLoading ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default FilesManagerPage;
